/**
 * Match-utkast: spelare plockade från Online-sidebaren till Skapa match.
 *
 * Sidebarens ➕-knapp lägger spelare här (sessionStorage, en flik/session),
 * och CreateMatch förbockar dem när spelarlistan laddats. Pub-sub-mönstret
 * speglar ``queueEventBus`` så sidebar och wizard kan hålla sig synkade utan
 * gemensam React-context.
 *
 * Utkastposter: { discord_id, steam_id, name } — discord_id är nyckeln
 * eftersom CreateMatch identifierar spelare på det fältet.
 */

const STORAGE_KEY = 'match_draft_players';

let subscribers = new Set();

const read = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || [];
  } catch {
    return [];
  }
};

const write = (players) => {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(players));
  for (const handler of subscribers) {
    try {
      handler(players);
    } catch (e) {
      console.error('match draft handler error', e);
    }
  }
};

/**
 * Subscribe to draft changes (add/remove/clear).
 *
 * @param {Function} handler - Called with the current draft array.
 * @returns {Function} Unsubscribe function.
 */
export function subscribeMatchDraft(handler) {
  subscribers.add(handler);
  return () => subscribers.delete(handler);
}

/**
 * Get the current draft.
 *
 * @returns {Array<{discord_id: string, steam_id: string, name: string}>}
 */
export function getMatchDraft() {
  return read();
}

/**
 * Check whether a player is already in the draft.
 *
 * @param {string} discordId - Player's discord_id.
 * @returns {boolean}
 */
export function isInMatchDraft(discordId) {
  return read().some((p) => String(p.discord_id) === String(discordId));
}

/**
 * Toggle a player in/out of the draft (➕ ↔ ✓ in the sidebar).
 *
 * @param {{discord_id: string, steam_id?: string, name?: string}} player
 * @returns {boolean} true if the player is in the draft after the toggle.
 */
export function toggleMatchDraft(player) {
  if (!player || !player.discord_id) return false;
  const draft = read();
  const idx = draft.findIndex(
    (p) => String(p.discord_id) === String(player.discord_id),
  );
  if (idx >= 0) {
    draft.splice(idx, 1);
    write(draft);
    return false;
  }
  draft.push({
    discord_id: String(player.discord_id),
    steam_id: player.steam_id || '',
    name: player.name || '',
  });
  write(draft);
  return true;
}

/**
 * Clear the draft — called by CreateMatch once the draft has been consumed
 * into its selection (and on match creation).
 */
export function clearMatchDraft() {
  write([]);
}
